"use client";
import * as React from "react";
import { useState } from "react";
import { Clipboard, SquareTerminal, Check } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { datadogLogs } from "@datadog/browser-logs";

interface CommandItem {
  label: string;
  command: string;
}

interface CommandBlockProps {
  title?: string;
  command?: string;
  commands?: CommandItem[];
  defaultValue?: string;
  showTerminalIcon?: boolean;
  className?: string;
}

export const SingleCommandBlock = ({
  title,
  command,
  showTerminalIcon = true,
  className,
}: {
  title?: string;
  command: string;
  showTerminalIcon?: boolean;
  className?: string;
}): React.ReactElement => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      datadogLogs.logger.error("Failed to copy text: ", { error: String(err) });
    }
  };

  return (
    <div className={cn("w-full max-w-2xl dark", className)}>
      <div className="bg-background rounded-md border pl-4 pr-2 py-1.5">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-2 flex-1 min-w-0 my-1.5">
            <span className="text-muted-foreground font-mono">$</span>
            <p className="text-foreground break-words whitespace-pre-wrap font-mono flex-1">
              {command}
            </p>
          </div>
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground ml-2 flex-shrink-0"
                onClick={() => copyToClipboard(command)}
              >
                {copied ? <Check size={18} /> : <Clipboard size={20} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{copied ? "Copied!" : "Copy to Clipboard"}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export const MultiCommandBlock = ({
  commands,
  defaultValue,
  showTerminalIcon = true,
  className,
}: {
  commands: CommandItem[];
  defaultValue?: string;
  showTerminalIcon?: boolean;
  className?: string;
}): React.ReactElement => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(
    defaultValue || commands[0]?.label,
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      datadogLogs.logger.error("Failed to copy text: ", { error: String(err) });
    }
  };

  const activeCommand =
    commands.find((cmd) => cmd.label === activeTab)?.command || "";

  return (
    <div className={cn("w-full max-w-2xl", className)}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full bg-[var(--offwhite)] rounded-md border gap-0"
      >
        <TabsList className="flex w-full bg-[var(--offwhite)] justify-between items-center px-2 py-1 my-1 rounded-t-md">
          <div className="flex items-center">
            {showTerminalIcon && (
              <SquareTerminal className="text-muted-foreground" size={20} />
            )}
            <div className="flex">
              {commands.map((tab) => (
                <TabsTrigger
                  key={tab.label}
                  value={tab.label}
                  className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </div>
          </div>
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => copyToClipboard(activeCommand)}
              >
                {copied ? <Check size={20} /> : <Clipboard size={20} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{copied ? "Copied!" : "Copy to Clipboard"}</p>
            </TooltipContent>
          </Tooltip>
        </TabsList>
        <Separator />
        {commands.map((tab) => (
          <TabsContent
            key={tab.label}
            value={tab.label}
            className="mt-0 px-4 py-2 font-mono bg-[var(--offwhite)] rounded-b-md overflow-x-auto"
          >
            <p className="text-muted-foreground break-words whitespace-pre-wrap">
              {tab.command}
            </p>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const CommandBlock = (props: CommandBlockProps): React.ReactElement => {
  const { command, commands, ...rest } = props;

  if (command) {
    return <SingleCommandBlock command={command} {...rest} />;
  }

  if (commands && commands.length > 0) {
    return <MultiCommandBlock commands={commands} {...rest} />;
  }

  return <></>;
};
